import RetailPOSWorkspaceRuntimeBoundary
  from "./RetailPOSWorkspaceRuntimeBoundary";

import {
  createRetailPOSDomainAdapter,
} from "./retailPOSDomainAdapter";

import {
  createRetailPOSProductRuntime,
} from "./retailPOSProductRuntime";

import {
  createRetailPOSSessionBridge,
} from "./retailPOSSessionRuntime";


/**
 * Retail POS Domain Composition.
 *
 * This is the production-ready composition boundary between
 * the generic POS Domain Framework and the Retail React
 * Runtime boundary.
 *
 * It does NOT:
 *
 * - create a POSActivityHost;
 * - create a global POS registry;
 * - own publication state;
 * - own Product state;
 * - own Cart state;
 * - own Customer state;
 * - own Loyalty state;
 * - own Receipt state;
 * - own CashSession state.
 *
 * The application-level POS host remains responsible for
 * registering this domain adapter in POSDomainRegistry.
 */
export function createRetailPOSDomainComposition({
  catalogProvider = null,
  sessionBridge = null,

  productResolver = null,
  productSearchProvider = null,

  workspaceComponent = null,

  dispose = null,
} = {}) {
  const productRuntime =
    createRetailPOSProductRuntime();

  const resolvedProductResolver =
    productResolver
    ?? productRuntime
      .resolveProductByCode;

  const resolvedProductSearchProvider =
    productSearchProvider
    ?? productRuntime
      .searchProducts;

  const resolvedSessionBridge =
    sessionBridge
    ?? createRetailPOSSessionBridge();

  const WorkspaceComponent =
    workspaceComponent
    ?? createRetailPOSRuntimeBoundary({
      productResolver:
        resolvedProductResolver,

      productSearchProvider:
        resolvedProductSearchProvider,

      sessionBridge:
        resolvedSessionBridge,
    });

  const domainAdapter =
    createRetailPOSDomainAdapter({
      catalogProvider,
      sessionBridge,

      workspaceComponent:
        WorkspaceComponent,

      dispose,
    });

  return Object.freeze({
    domainAdapter,
    workspaceComponent:
      WorkspaceComponent,
  });
}


/**
 * Bind optional non-React Retail capabilities to the React
 * Runtime Boundary while preserving POSActivityHost props.
 *
 * Customer, Loyalty, Receipt and Cart are deliberately NOT
 * wired here:
 *
 * - Cart comes from CartContext through useRetailPOSRuntime();
 * - Customer/Loyalty/Receipt use their certified Runtime
 *   adapters from useRetailPOSRuntime().
 */
export function createRetailPOSRuntimeBoundary({
  productResolver = null,
  productSearchProvider = null,
  sessionBridge = null,
} = {}) {
  function BoundRetailPOSRuntimeBoundary(
    props
  ) {
    return (
      <RetailPOSWorkspaceRuntimeBoundary
        {...props}

        productResolver={
          productResolver
        }

        productSearchProvider={
          productSearchProvider
        }

        sessionBridge={
          sessionBridge
        }
      />
    );
  }

  BoundRetailPOSRuntimeBoundary.displayName =
    "BoundRetailPOSRuntimeBoundary";

  return BoundRetailPOSRuntimeBoundary;
}
